import { Checkbox } from '@ets/design-system';

import EventsManager from 'Editor/services/EventsManager';
import { ELEMENTS } from 'Editor/services/consts';
import EditorManager from 'Editor/services/EditorManager';
import { SuiteAvatar } from '_common/suite/components';

import styles from './ApproveViewElement.module.scss';
import { EditorDOMUtils } from 'Editor/services/_Common/DOM';
import { BaseBlockElement } from '../../../ViewBuilders';
import { Root } from 'react-dom/client';
import ReactUtils from '_common/utils/ReactUtils';
import { FormattedMessage } from 'react-intl';

type ApproveUserListType = {
  usersApprovedList: { user: UserId; time: string }[];
};

const ApproveUserListComponent = ({ usersApprovedList }: ApproveUserListType) => {
  return (
    <>
      {usersApprovedList.length > 0 && (
        <span className={styles.approvedByTextElement}>
          <FormattedMessage id="storage.actionBar.filters.approvedBy" />:
        </span>
      )}
      {usersApprovedList.map(({ user }) => (
        <span key={user} className={styles.userElement}>
          <SuiteAvatar size="s" userId={user} showTooltip />
        </span>
      ))}
    </>
  );
};

export class ApproveViewElement extends BaseBlockElement {
  static SELECTION: Editor.Decorator.Selection = {};
  static SELECTING: boolean = false;

  static handleMouseDown() {
    ApproveViewElement.SELECTING = false;

    EventsManager.getInstance().on('DOCUMENT_MOUSE_UP', ApproveViewElement.handleMouseUp);
  }

  static handleMouseUp(e: MouseEvent) {
    let element = e.target as HTMLElement;
    const target = e.target as HTMLElement;

    if (element.tagName !== ELEMENTS.ApproveViewElement.TAG) {
      element = EditorDOMUtils.closest(
        element,
        ELEMENTS.ApproveViewElement.TAG,
      ) as ApproveViewElement;
    }

    if (!ApproveViewElement.SELECTING && element) {
      if (element && element instanceof ApproveViewElement) {
        if (
          document
            .getElementById(`ApprovalIconWrapper-${element.getAttribute('enclosed_element')}`)
            ?.contains(target)
        ) {
          element.toggleSelected();
        } else {
          EditorManager.getInstance().deselectAllForApproval();
          element.selected = true;
        }
      }
    }

    // publish select bulk action
    const keys = Object.keys(ApproveViewElement.SELECTION);

    if (keys.length > 0) {
      const selectedElements = [];
      for (let i = 0; i < keys.length; i += 1) {
        const id = keys[i];
        if (ApproveViewElement.SELECTION[id]) {
          selectedElements.push(id);
        }
      }
      EditorManager.getInstance().selectNodesForApproval(selectedElements);
    }

    ApproveViewElement.SELECTING = false;

    EventsManager.getInstance().removeListener(
      'DOCUMENT_MOUSE_UP',
      ApproveViewElement.handleMouseUp,
    );
  }

  static get observedAttributes() {
    return ['selected'];
  }

  private rootReactIconContainer!: Root;
  private rootReactUsersContainer!: Root;

  private container!: HTMLDivElement;
  private iconWrapper!: HTMLDivElement;
  private contentWrapper!: HTMLDivElement;
  private users!: HTMLSpanElement;
  private content!: HTMLSpanElement;

  private nodeContent: HTMLElement | null;
  private approvedUsers: { user: UserId; time: string }[];

  constructor() {
    super();
    this.nodeContent = null;
    this.approvedUsers = [];

    this.handleSelectedAttributeChange = this.handleSelectedAttributeChange.bind(this);
  }

  get selected() {
    return this.hasAttribute('selected');
  }

  set selected(val: any) {
    if (val) {
      this.setAttribute('selected', 'true');
    } else {
      this.removeAttribute('selected');
    }
  }

  attributeChangedCallback(name: string, oldValue: any, newValue: any) {
    if (this.isConnected) {
      if (name === 'selected' && oldValue !== newValue) {
        this.handleSelectedAttributeChange(newValue);
      } else {
        super.attributeChangedCallback(name, oldValue, newValue);
      }
    }
  }

  deselect() {
    this.removeAttribute('selected');
  }

  handleSelectedAttributeChange(newValue: any) {
    const enclosedNodeId: string = this.getAttribute('enclosed_element')!;
    if (newValue) {
      this.setIcon();
      ApproveViewElement.SELECTION[enclosedNodeId] = true;
    } else {
      this.setIcon();
      ApproveViewElement.SELECTION[enclosedNodeId] = false;
    }
  }

  private applyStyles() {
    const approved = this.approvedUsers && this.approvedUsers.length > 0 ? 'true' : 'false';
    const selected = this.selected ? 'true' : 'false';

    this.contentWrapper.setAttribute('data-selected', selected);
    this.contentWrapper.setAttribute('data-approved', approved);

    this.content.setAttribute('data-selected', selected);
    this.content.setAttribute('data-approved', approved);

    this.users.setAttribute('data-selected', selected);
    this.users.setAttribute('data-approved', approved);
  }

  toggleSelected() {
    this.selected = !this.selected;
  }

  setContent(child: HTMLElement | null) {
    this.nodeContent = child;
  }

  /**
   * NOTE:
   * Approve works differently, that permission.
   * So this function is only invocked is the contruction of the element and not when the node is updated
   * */

  setApprovalUsers(users: { user: UserId; time: string }[]) {
    this.approvedUsers = users;
  }

  updateApproved() {
    this.users.style.display = this.approvedUsers.length > 0 ? 'flex' : 'none';
    ReactUtils.renderReactComponent(
      <ApproveUserListComponent usersApprovedList={this.approvedUsers} />,
      this.rootReactUsersContainer,
    );
  }

  setIcon() {
    this.applyStyles();
    ReactUtils.renderReactComponentWithoutRedux(
      <Checkbox
        checked={this.selected}
        size="s"
        onChange={() => {}}
        testId={`checkbox-column-${this.getAttribute('enclosed_element')}`}
      />,
      this.rootReactIconContainer,
    );
  }

  connectedCallback() {
    super.connectedCallback();

    // clean childreen
    while (this.firstChild) {
      this.removeChild(this.firstChild);
    }

    this.addEventListener('mousedown', ApproveViewElement.handleMouseDown);

    this.container = document.createElement('div');
    this.container.setAttribute('class', styles.container);
    this.container.setAttribute(
      'data-testid',
      `approve-view-element-${this.getAttribute('enclosed_element')}`,
    );

    this.iconWrapper = document.createElement('div');
    this.iconWrapper.setAttribute('class', styles.iconWrapper);
    this.iconWrapper.setAttribute(
      'id',
      `ApprovalIconWrapper-${this.getAttribute('enclosed_element')}`,
    );

    this.contentWrapper = document.createElement('div');
    this.contentWrapper.setAttribute('class', styles.contentWrapper);

    this.content = document.createElement('span');
    this.content.setAttribute('class', styles.content);

    this.users = document.createElement('span');
    this.users.setAttribute('class', styles.users);

    this.appendChild(this.container);
    this.container.appendChild(this.iconWrapper);
    this.container.appendChild(this.contentWrapper);
    this.contentWrapper.appendChild(this.content);
    this.contentWrapper.appendChild(this.users);

    if (this.nodeContent) {
      this.content.appendChild(this.nodeContent);
    }

    if (!this.rootReactIconContainer) {
      this.rootReactIconContainer = ReactUtils.createReactRoot(this.iconWrapper);
    }
    if (!this.rootReactUsersContainer) {
      this.rootReactUsersContainer = ReactUtils.createReactRoot(this.users);
    }

    this.updateApproved();
    this.setIcon();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    ReactUtils.unmountReactComponent(this.rootReactIconContainer);
    ReactUtils.unmountReactComponent(this.rootReactUsersContainer);
    this.removeEventListener('mousedown', ApproveViewElement.handleMouseDown);
  }
}

if (!window.customElements.get(ELEMENTS.ApproveViewElement.IDENTIFIER)) {
  window.customElements.define(ELEMENTS.ApproveViewElement.IDENTIFIER, ApproveViewElement);
}
